import {
  Alert,
  Button,
  List,
  ListItem,
  Stack,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { tournamentApi } from "@bw/shared/api";
import { queryKeys } from "../../../common/query/queryClient";

export function TeamDrawResults({ drawId }: { drawId: string }) {
  const [params, setParams] = useSearchParams();
  const rawPage = Number(params.get("team_match_page"));
  const page = Number.isSafeInteger(rawPage) && rawPage > 0 ? rawPage : 1;
  const matches = useQuery({
    queryKey: [...queryKeys.tournaments.all, "team-matches", drawId, page],
    queryFn: () =>
      tournamentApi.getTeamMatches(drawId, { page, page_size: 20 }),
  });
  function changePage(value: number) {
    const next = new URLSearchParams(params);
    next.set("tab", "draws");
    next.set("draw_id", drawId);
    next.set("team_match_page", String(value));
    setParams(next);
  }
  return (
    <Stack component="section" aria-label="Team results" spacing={1.5}>
      <Typography component="h3" variant="h6">
        Team results
      </Typography>
      {matches.isLoading ? (
        <Typography role="status">Loading team matches…</Typography>
      ) : matches.isError ? (
        <Alert
          severity="error"
          action={<Button onClick={() => void matches.refetch()}>Retry</Button>}
        >
          Could not load team results.
        </Alert>
      ) : !matches.data?.items.length ? (
        <Typography>No team matches on this page.</Typography>
      ) : (
        <List disablePadding>
          {matches.data.items.map((match) => (
            <ListItem
              key={match.team_match_id}
              divider
              sx={{ display: "block", py: 2 }}
            >
              <Stack spacing={0.75} sx={{ overflowWrap: "anywhere" }}>
                <Typography variant="caption">
                  Round {match.round_number} · Match {match.position}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  sx={{ flexWrap: "wrap", alignItems: "center" }}
                >
                  <Typography sx={{ fontWeight: 600 }}>
                    {match.home_team_name ??
                      (match.home_team_id
                        ? "Team unavailable"
                        : "Team to be determined")}
                  </Typography>
                  <Typography variant="caption">vs</Typography>
                  <Typography sx={{ fontWeight: 600 }}>
                    {match.away_team_name ??
                      (match.away_team_id
                        ? "Team unavailable"
                        : "Team to be determined")}
                  </Typography>
                </Stack>
                <Typography variant="body2">
                  {match.status.replaceAll("_", " ")} ·{" "}
                  {match.outcome.replaceAll("_", " ")}
                </Typography>
                {match.score_home != null || match.score_away != null ? (
                  <Typography>
                    Rubbers won: {match.score_home ?? "—"} –{" "}
                    {match.score_away ?? "—"}
                  </Typography>
                ) : null}
                {match.winner_team_name ? (
                  <Typography>Winner: {match.winner_team_name}</Typography>
                ) : null}
              </Stack>
            </ListItem>
          ))}
        </List>
      )}
      {matches.data &&
      !matches.isError &&
      (page > 1 || page * matches.data.page_size < matches.data.total) ? (
        <Stack direction="row" spacing={1}>
          <Button disabled={page === 1} onClick={() => changePage(page - 1)}>
            Previous team matches
          </Button>
          <Button
            disabled={page * matches.data.page_size >= matches.data.total}
            onClick={() => changePage(page + 1)}
          >
            Next team matches
          </Button>
        </Stack>
      ) : null}
    </Stack>
  );
}
